// src/pages/RideDriverSetup.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_WRK = process.env.REACT_APP_API_WRK;

const RideDriverSetup = () => {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [carPhotoFile, setCarPhotoFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");

  const workerId = localStorage.getItem("workerId");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDriverProfile = async () => {
      try {
        const res = await axios.get(`${API_WRK}/profile/${workerId}`);
        const dp = res.data?.driverProfile;

        if (
          dp?.make &&
          dp?.model &&
          dp?.year &&
          dp?.licenseUrl &&
          dp?.insuranceUrl &&
          dp?.carPhotoUrl &&
          dp.status
        ) {
          setStatus(dp.status);
        } else {
          setStatus(null); // force reupload if incomplete
        }
      } catch (err) {
        console.error("Failed to fetch driver profile:", err);
      }
    };
    if (workerId) fetchDriverProfile();
  }, [workerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!make || !model || !year || !licenseFile || !insuranceFile || !carPhotoFile) {
      setMessage("⚠️ All fields and documents are required.");
      return;
    }

    const formData = new FormData();
    formData.append("workerId", workerId);
    formData.append("make", make);
    formData.append("model", model);
    formData.append("year", year);
    formData.append("licenseFile", licenseFile);
    formData.append("insuranceFile", insuranceFile);
    formData.append("carPhotoFile", carPhotoFile);

    try {
      await axios.post(`${API_WRK}/driver-docs`, formData);
      setStatus("pending");
      setMessage("✅ Your documents are under review. You’ll be able to post a ride once approved.");
    } catch (err) {
      console.error("Upload failed:", err);
      setMessage("❌ Failed to upload documents.");
    }
  };

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center text-center px-6">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-xl">
          <h2 className="text-2xl font-bold text-yellow-600 mb-4">
            ⏳ Documents Under Review
          </h2>
          <p className="text-gray-700">
            You’ve already submitted your driver documents. We’ll notify you once approved.
            You’ll be able to post rides as soon as your profile is verified.
          </p>
        </div>
      </div>
    );
  }

  if (status === "approved" && !localStorage.getItem("seenRideApproved")) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center text-center px-6">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-xl">
          <h2 className="text-2xl font-bold text-green-600 mb-4">✅ You’re Approved!</h2>
          <p className="text-gray-700 mb-6">
            Your driver documents have been approved. You can now post and manage rides in your dashboard.
          </p>
          <button
            onClick={() => {
              localStorage.setItem("seenRideApproved", "true");
              navigate("/rides/dashboard");
            }}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  if (status === "approved") {
    navigate("/rides/dashboard");
    return null;
  }

  return (
    <div className="min-h-screen bg-blue-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-blue-700 mb-6">
          🚘 Driver Profile Setup
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Car Info */}
          <div>
            <label className="block mb-1 font-medium">Car Make</label>
            <input
              type="text"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              className="w-full border rounded px-4 py-2"
              placeholder="e.g. Toyota"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Car Model</label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full border rounded px-4 py-2"
              placeholder="e.g. Corolla"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Car Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full border rounded px-4 py-2"
              placeholder="e.g. 2020"
              required
            />
          </div>

          {/* Driver License Upload */}
          <div>
            <label className="block mb-1 font-medium">Upload Driver License</label>
            <div className="border-2 border-dashed border-blue-400 rounded-lg p-4 bg-blue-50 text-center cursor-pointer hover:bg-blue-100">
              <input
                type="file"
                id="licenseUpload"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => setLicenseFile(e.target.files[0])}
                required
              />
              <label htmlFor="licenseUpload" className="cursor-pointer text-blue-700 font-semibold">
                Upload License (PDF/Image)
              </label>
              {licenseFile && <p className="text-green-600 mt-2 text-sm">✅ {licenseFile.name}</p>}
            </div>
          </div>

          {/* Insurance Upload */}
          <div>
            <label className="block mb-1 font-medium">Upload Insurance Paper</label>
            <div className="border-2 border-dashed border-blue-400 rounded-lg p-4 bg-blue-50 text-center cursor-pointer hover:bg-blue-100">
              <input
                type="file"
                id="insuranceUpload"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => setInsuranceFile(e.target.files[0])}
                required
              />
              <label htmlFor="insuranceUpload" className="cursor-pointer text-blue-700 font-semibold">
                Upload Insurance (PDF/Image)
              </label>
              {insuranceFile && <p className="text-green-600 mt-2 text-sm">✅ {insuranceFile.name}</p>}
            </div>
          </div>

          {/* Car Photo Upload */}
          <div>
            <label className="block mb-1 font-medium">Upload Car Photo</label>
            <div className="border-2 border-dashed border-blue-400 rounded-lg p-4 bg-blue-50 text-center cursor-pointer hover:bg-blue-100">
              <input
                type="file"
                id="photoUpload"
                accept="image/*"
                className="hidden"
                onChange={(e) => setCarPhotoFile(e.target.files[0])}
                required
              />
              <label htmlFor="photoUpload" className="cursor-pointer text-blue-700 font-semibold">
                Upload Photo (JPG/PNG)
              </label>
              {carPhotoFile && <p className="text-green-600 mt-2 text-sm">✅ {carPhotoFile.name}</p>}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg"
          >
            Upload Documents
          </button>

          {message && <p className="mt-4 text-center font-medium text-blue-800">{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default RideDriverSetup;
